import React from 'react';
import _, { isNumber, isArray } from 'lodash';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import {
    useInfiniteQuery,
    UseInfiniteQueryOptions,
    UseInfiniteQueryResult,
    useQuery,
    UseQueryResult
} from '@tanstack/react-query';
import { DateService, getRulesHistory, getRulesHistoryCount, NumberService } from 'Services';
import { RQ_HISTORY, RQ_HISTORY_COUNT, CONDITION_NAME_REGEX } from 'Utils';
import { PARAMETERS_PARAM_TYPES, RULE_INPUT_PARAMETER_NAMES } from 'Common/enums';

function parameterChangeTranslate(t: TFunction, field: string, previousValue: any, value: any) {
    if (previousValue === undefined || previousValue === null) {
        if (_.isNumber(value)) {
            return t('paramAdd', {
                field: _.upperFirst(field),
                value: NumberService.getLocaleNumber(value)
            });
        }
        return t('paramAdd', { field: _.upperFirst(field), value });
    }

    if (value === undefined || value === null) return t('paramDelete', { field: _.upperFirst(field) });

    if (_.isNumber(value) && previousValue) {
        return t('paramChange', {
            field: _.upperFirst(field),
            value: NumberService.getLocaleNumber(value),
            previousValue: NumberService.getLocaleNumber(previousValue)
        });
    }

    return t('paramChange', { field: _.upperFirst(field), value, previousValue });
}

function conclusionChangeTraslate(t: TFunction, previousValue: string[], value: string[]) {
    const secondaryConclusion = value?.slice(1).map((iteam: string) => {
        return iteam;
    });
    const secondaryConclusionPreviousValues = previousValue?.slice(1).map((iteam: string) => {
        return iteam;
    });
    const conclusionpreviousValues =
        previousValue.length > 1
            ? `${previousValue[0]} ${t('And Set Flags')} [${secondaryConclusionPreviousValues.join(', ')}]`
            : previousValue;
    const changePrimaryConclusion = secondaryConclusion.length
        ? `${value[0]} ${t('And Set Flags')} [${secondaryConclusion.join(', ')}]`
        : value;
    if (!previousValue.length && value.length) {
        const primaryConclusion =
            value.length > 1 ? `${value[0]} ${t('And Set Flags')} [${secondaryConclusion.join(', ')}]` : value;
        return `${t('conclusionAdd', { value: primaryConclusion })}`;
    }
    return `${t('conclusionChange', {
        previousValue: conclusionpreviousValues,
        value: changePrimaryConclusion
    })}`;
}

function conditionChangeTranslate(
    t: TFunction,
    field: string,
    previousValue: any,
    value: any,
    previousOperator?: string | null,
    operator?: string | null,
    readOnly?: boolean | null | undefined,
    isCurrencyParameter?: boolean
) {
    const translationFcn = isCurrencyParameter ? NumberService.getLocaleCurrency : NumberService.getLocaleNumber;
    // handle readOnly change
    if (readOnly !== undefined) {
        return t('setReadOnly', { field, readOnly });
    }

    // previousValue is undef | null since condition initialized (added)
    if (previousValue === undefined || previousValue === null) {
        if (_.isNumber(value)) {
            return t('conditionAdd', {
                field: _.upperFirst(field),
                value: translationFcn(value),
                operator: t(`conditions.${operator}`)
            });
        }

        return t('conditionAdd', { field: _.upperFirst(field), value, operator: t(`conditions.${operator}`) });
    }

    // value is undef | null since condition is deleted
    if (value === undefined || value === null)
        return t('conditionDelete', {
            field: _.upperFirst(field),
            previousValue,
            previousOperator: t(`conditions.${previousOperator}`)
        });

    // if neither previousValue or value are undef | null, condition was changed
    if (_.isNumber(value)) {
        return t('conditionChange', {
            field: _.upperFirst(field),
            value: translationFcn(value),
            previousValue: translationFcn(previousValue),
            operator: t(`conditions.${operator}`),
            previousOperator: t(`conditions.${previousOperator}`)
        });
    }

    return t('conditionChange', {
        field: _.upperFirst(field),
        value,
        previousValue,
        operator: t(`conditions.${operator}`),
        previousOperator: t(`conditions.${previousOperator}`)
    });
}

function listChangeTranslate(
    t: TFunction,
    listName: string,
    previousValue: any,
    value: any,
    isTenantWithLandlord: boolean,
    listData?: ListManagementApiList[]
) {
    const listIds = value?.listIds || [];
    const prevListIds = previousValue?.listIds || [];
    let listNames = listIds
        .map((id: string) => listData?.find((list: ListManagementApiList) => String(list.id) === String(id))?.name)
        .join('; ');

    // if the listId doesnt exist for the current tenant, assume that it is a landlord list.
    if (listNames.length === 0) {
        /**
         * Templated Rules (TR) using External List and clients using TR will not be a Tenant with Landlord.
         * If the client will use adhoc (simple or advanced) rules Templated rules will be turned off.
         */
        listNames = isTenantWithLandlord ? t('Network List') : t('External List');
    }

    if (prevListIds.length) {
        const prevListNames = prevListIds
            .map((id: string) => listData?.find((list: ListManagementApiList) => String(list.id) === String(id))?.name)
            .join('; ');

        if (listIds.length) {
            return t('listChange', {
                fieldName: _.upperFirst(listName),
                value: listNames,
                previousValue: prevListNames,
                operator: t(`conditions.${value.operator}`),
                previousOperator: t(`conditions.${previousValue.operator}`)
            });
        }

        return t('listDelete', {
            fieldName: _.upperFirst(listName),
            previousValue: prevListNames,
            previousOperator: t(`conditions.${previousValue.operator}`)
        });
    }
    return t('listAdd', {
        fieldName: _.upperFirst(listName),
        value: listNames,
        operator: t(`conditions.${value.operator}`)
    });
}

const changeDetailAsString = (
    t: TFunction,
    isTenantWithLandlord: boolean,
    listData?: ListManagementApiList[],
    fieldNameHashmap?: ParameterFieldNameHashmap,
    globalParameterTypeMap?: { [key: string]: string }
) => ({
    field,
    value,
    previousValue,
    operator,
    previousOperator,
    readOnly,
    fieldName,
    isTemplateCondition
}: {
    field: string;
    value: any;
    previousValue: any;
    operator?: string | null;
    previousOperator?: string | null;
    readOnly?: boolean | null | undefined;
    fieldName?: string;
    isTemplateCondition?: boolean | undefined;
}) => {
    // handle typeof number
    const pMatch = field.match(CONDITION_NAME_REGEX);
    const trimmedParameter = pMatch && pMatch.length > 1 ? pMatch[1] : '';
    let isCurrencyParameter;
    if (trimmedParameter) {
        const adhocParamType =
            fieldNameHashmap && fieldNameHashmap[trimmedParameter]
                ? fieldNameHashmap[trimmedParameter].type
                : undefined;
        const globalParamType = globalParameterTypeMap ? globalParameterTypeMap[field] : undefined;
        isCurrencyParameter =
            adhocParamType === PARAMETERS_PARAM_TYPES.CURRENCY ||
            globalParamType === RULE_INPUT_PARAMETER_NAMES.CURRENCY;
    } else {
        isCurrencyParameter = false;
    }
    switch (field) {
        case 'active':
            if (previousValue !== null) {
                return t('statusChange', { status: value ? t('Active') : t('Inactive') });
            }
            return t('statusAdd', { status: value ? t('Active') : t('Inactive') });
        case 'score': {
            // handling for template score conclusions - checks for score, ACCEPT, REJECT conditions with isNumber()
            if (!isNumber(previousValue) && isNumber(value)) return t('conclusionScoreAdd', { value });
            if (!isNumber(previousValue) && !isNumber(value)) return t('conclusionAdd', { value });
            if (isNumber(previousValue) && !isNumber(value)) return t('conclusionChange', { value, previousValue });
            return t('conclusionScoreChange', { value, previousValue });
        }
        case 'currency':
            return t('paramChange', {
                field: t('Threshold'),
                value: NumberService.getLocaleCurrency(value)
            });
        case 'enum': {
            switch (value) {
                case 'OVERRIDE_ADDED':
                    return t('Override added');
                case 'OVERRIDE_REMOVED':
                    return t('Override removed');
                default:
                    return value;
            }
        }
        case 'comment': {
            return t('commentAdded', { value });
        }
        case 'priority': {
            if (previousValue != null) return t('executionOrderChange', { value, previousValue });

            return t('executionOrderAdded', { value });
        }
        case 'list': {
            let listName = fieldName || '';
            const match = listName.match(CONDITION_NAME_REGEX);
            if (match && match.length > 0) {
                const regexFieldMatch = match![1];
                listName = fieldNameHashmap?.[regexFieldMatch]?.message ?? '';
            }

            return listChangeTranslate(t, listName, previousValue, value, isTenantWithLandlord, listData);
        }
        case 'ruleGroup': {
            return parameterChangeTranslate(t, t('Rule Group'), previousValue, value);
        }
        case 'conclusion': {
            if (isArray(value)) return conclusionChangeTraslate(t, previousValue, value);

            return parameterChangeTranslate(t, field, previousValue, value);
        }
        default: {
            // template conditions are returned with a different structure
            // so we use fieldName instead of trying to regex match and lookup like for adhoc in the else block
            if (isTemplateCondition) {
                const fieldMessage = fieldName ?? field;
                return conditionChangeTranslate(
                    t,
                    fieldMessage,
                    previousValue,
                    value,
                    previousOperator,
                    operator,
                    readOnly,
                    isCurrencyParameter
                );
            }
            const match = field.match(CONDITION_NAME_REGEX);
            let conditionName;

            if (match && match.length > 0) {
                const regexFieldMatch = match![1];
                conditionName = fieldNameHashmap?.[regexFieldMatch]?.message ?? '';
                if (conditionName === '') {
                    // templated rule which is not in the parameter table
                    conditionName = fieldName ?? '';
                }
                return conditionChangeTranslate(
                    t,
                    conditionName,
                    previousValue,
                    value,
                    previousOperator,
                    operator,
                    readOnly,
                    isCurrencyParameter
                );
            }

            return parameterChangeTranslate(t, field, previousValue, value);
        }
    }
};

function getChangeActionAndChangeDetail(
    t: TFunction,
    isTenantWithLandlord: boolean,
    changeAction: string,
    receivedChangeDetails: {
        field: string;
        value: any;
        previousValue: any;
        operator?: string | null;
        previousOperator?: string | null;
        readOnly?: boolean | null | undefined;
        fieldName?: string;
        isTemplateCondition?: boolean | undefined;
    }[],
    listData?: ListManagementApiList[],
    fieldNameHashmap?: ParameterFieldNameHashmap,
    globalParameterTypeMap?: { [key: string]: string }
) {
    const detailString = changeDetailAsString(
        t,
        isTenantWithLandlord,
        listData,
        fieldNameHashmap,
        globalParameterTypeMap
    );
    const changeDetails = receivedChangeDetails.map(detailString);

    switch (changeAction) {
        case 'VERSION_DEPLOYED': {
            const [first] = _.toArray(receivedChangeDetails);
            return {
                changeAction: t('Rule Deployed'),
                changeDetails: [t('versionDeployed', { version: first?.value })]
            };
        }
        case 'GLOBAL_RULE_REVISED': {
            return {
                changeAction: t('Global Rule Revised'),
                changeDetails
            };
        }
        case 'GLOBAL_RULE_CREATED': {
            return {
                changeAction: t('Global Rule Created'),
                changeDetails
            };
        }
        case 'MERCHANT_OVERRIDE_REVISED': {
            return {
                changeAction: t('Merchant Override Revised'),
                changeDetails
            };
        }
        case 'MERCHANT_OVERRIDE_ADDED': {
            return {
                changeAction: t('Merchant Override Added'),
                changeDetails
            };
        }
        case 'TENANT_GROUP_OVERRIDE_REVISED': {
            return {
                changeAction: t('Tenant Group Override Revised'),
                changeDetails
            };
        }
        case 'TENANT_GROUP_OVERRIDE_ADDED': {
            return {
                changeAction: t('Tenant Group Override Added'),
                changeDetails
            };
        }
        case 'ADHOC_RULE_CREATED': {
            return {
                changeAction: t('Adhoc Rule Created'),
                changeDetails
            };
        }
        case 'ADHOC_RULE_DELETED': {
            return {
                changeAction: t('Adhoc Rule Deleted'),
                changeDetails: [t('statusChange', { status: t('Ready To Delete') }), ...changeDetails]
            };
        }
        default: {
            return {
                changeAction,
                changeDetails
            };
        }
    }
}

type Args = {
    queryOptions?: UseInfiniteQueryOptions<HistoryRulesResponse[]>;
    apiArgs: HistoryRulesParams;
    listData?: ListManagementApiList[];
    fieldNameHashmap?: ParameterFieldNameHashmap;
    globalParameterTypeMap?: { [key: string]: string };
    isTenantWithLandlord: boolean;
};

type UseRulesHistoryReturn = {
    infiniteQuery: UseInfiniteQueryResult<HistoryRulesResponse[], unknown>;
    countQuery: UseQueryResult<HistoryRulesCount, unknown>;
    order: Required<HistoryRulesParams['order']>;
    setOrder: React.Dispatch<React.SetStateAction<Required<HistoryRulesParams['order']>>>;
};
// query key based on version number as well
// the deployed versions number also uses this query
// YOU WANT TO USE THE revision NUMBER FOR max`Revision
function useRulesHistory({
    apiArgs: { perPage = 50, ...restApiArgs },
    queryOptions,
    listData,
    fieldNameHashmap,
    globalParameterTypeMap,
    isTenantWithLandlord
}: Args): UseRulesHistoryReturn {
    const [order, setOrder] = React.useState<Required<HistoryRulesParams['order']>>(restApiArgs?.order ?? 'desc');
    const { t } = useTranslation();
    const apiParams = { ...restApiArgs, perPage, order };
    const queryKey = [RQ_HISTORY, apiParams];
    const countQueryKey = [...queryKey, RQ_HISTORY_COUNT];
    const countQuery = useQuery(countQueryKey, () => getRulesHistoryCount({ ...apiParams }));
    const infiniteQuery = useInfiniteQuery(
        queryKey,
        ({ pageParam = 1 }) => getRulesHistory({ ...apiParams, page: pageParam }),
        {
            enabled: queryOptions?.enabled,
            getNextPageParam: (lastPage, allPages) => {
                return lastPage.length > 0 ? allPages.length + 1 : undefined;
            },
            select: (data) => {
                // group by the date only (6/25/2021) === June 25, 2021
                // the shape of the object should be:
                // { '6/25/2021': HistoryRulesResponse[]}
                const custom: { [key: string]: HistoryRulesResponse[] } = _.groupBy(
                    _.map(_.flattenDeep(data.pages), (o) => {
                        return {
                            ...o,
                            _formatted: {
                                time: DateService.getTime(o.createdAt),
                                ...getChangeActionAndChangeDetail(
                                    t,
                                    isTenantWithLandlord,
                                    o.changeAction,
                                    o.changeDetails,
                                    listData,
                                    fieldNameHashmap,
                                    globalParameterTypeMap
                                )
                            }
                        };
                    }),
                    (o) => DateService.getDate(o.createdAt)
                );
                return {
                    ...data,
                    custom
                };
            }
        }
    );

    return {
        infiniteQuery,
        countQuery,
        setOrder,
        order
    };
}

export default useRulesHistory;
